/**
 * @file OperationsIssuesPage.jsx
 * @description OPERATIONS department — Issues: the automatic issue raising of
 *              the telematics plan. An issue (a machine silent inside its
 *              shift, a serious fault code on a machine at work, or one raised
 *              here by hand) is put to the SUPERVISOR first, then the MANAGER,
 *              then the HEAD — each gets an in-app message and then an in-app
 *              call before it moves up; past the head it goes to the management
 *              mailbox. A reply from anyone on the ladder answers it.
 *
 *              Top: the escalation chain — who sits on each rung, by default
 *              and per customer. Below: the issues and how far each one went.
 *
 * This file belongs to the OPERATIONS department; the ladder runs in the Fleet
 * Agent (server services/agentWorkflow.js, /api/agent/issues + /escalation-chain).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Card, Stack, Chip, Skeleton, Alert, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, Snackbar, Autocomplete, TextField, MenuItem, IconButton,
    Tooltip, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
    ToggleButtonGroup, ToggleButton, CircularProgress,
} from '@mui/material';
import {
    AddAlert as RaiseIcon, TaskAlt as ResolveIcon, Add as AddIcon,
    DeleteOutline as DeleteIcon, MarkEmailRead as MailIcon,
    NotificationImportant as EmptyIcon,
} from '@mui/icons-material';
import agentService from '../../../services/agentService';
import { formatToIST } from '../../../utils/dataUtils';

const LEVELS = ['SUPERVISOR', 'MANAGER', 'HEAD'];
const LEVEL_LABEL = { SUPERVISOR: 'Supervisor', MANAGER: 'Manager', HEAD: 'Head' };
const SEVERITY = { critical: 'error', high: 'error', medium: 'warning', low: 'info' };
const STATUS = {
    OPEN: { c: 'warning', l: 'Open' }, ANSWERED: { c: 'info', l: 'Answered' },
    RESOLVED: { c: 'success', l: 'Resolved' }, EXPIRED: { c: 'default', l: 'Expired' },
};

// "API Error: 400 Bad Request - {"error":"…"}" → "…"
const errText = (e, fallback) => {
    const raw = String(e?.message || '');
    try { return JSON.parse(raw.slice(raw.indexOf(' - ') + 3)).error || fallback; } catch { return raw || fallback; }
};

const reasonLabel = (i) => i.reason === 'NO_FRAME' ? 'No data'
    : i.reason === 'FAULT' ? 'Fault code'
    : i.reason === 'MANUAL' ? `Raised by ${i.raised_by_name || 'Operations'}` : i.reason;

/** One rung's state on one issue: { label, color, who }. */
function rungState(issue, level) {
    // Issues from before the ladder carry only the supervisor, on the row itself.
    const steps = issue.steps.length ? issue.steps
        : (issue.supervisor_name && issue.asked_at ? [{ level: 'SUPERVISOR', name: issue.supervisor_name,
            messaged_at: issue.asked_at, called_at: issue.escalated_at,
            outcome: issue.answered_at ? 'ANSWERED' : null }] : []);
    const s = steps.filter(x => x.level === level).pop();
    if (!s) {
        const later = steps.some(x => LEVELS.indexOf(x.level) > LEVELS.indexOf(level)) || issue.stage === 'MANAGEMENT';
        return later ? { label: 'Nobody set', color: 'default', who: null } : { label: 'Not reached', color: 'default', who: null };
    }
    const live = issue.status === 'OPEN';
    if (s.outcome === 'ANSWERED') return { label: 'Answered', color: 'success', who: s.name };
    if (s.outcome === 'MISSED') return { label: 'Missed call', color: 'error', who: s.name };
    if (s.outcome === 'DECLINED') return { label: 'Declined', color: 'error', who: s.name };
    if (s.outcome === 'NO_REPLY') return { label: 'No answer', color: 'error', who: s.name };
    if (s.called_at) return { label: live ? 'Calling' : 'Called', color: live ? 'warning' : 'default', who: s.name };
    return { label: live ? 'Messaged' : 'Asked', color: live ? 'info' : 'default', who: s.name };
}

// The rungs top to bottom, in the order the issue climbs them.
const Ladder = ({ issue }) => (
    <Stack spacing={0.5} alignItems="flex-start">
        {LEVELS.map(level => {
            const st = rungState(issue, level);
            return (
                <Stack key={level} direction="row" alignItems="center" spacing={0.75}>
                    <Chip size="small" color={st.color} variant={st.color === 'default' ? 'outlined' : 'filled'}
                        label={`${LEVEL_LABEL[level]} · ${st.label}`} sx={{ fontWeight: 600 }} />
                    {st.who && <Typography variant="caption" color="text.secondary" noWrap>{st.who}</Typography>}
                </Stack>
            );
        })}
        {issue.stage === 'MANAGEMENT' && (
            <Chip size="small" icon={<MailIcon />} variant="outlined" label="Management mailed" sx={{ fontWeight: 600 }} />
        )}
    </Stack>
);

// ── The escalation chain editor ─────────────────────────────────────────────
// Rows: [{ scope: '' | customer, SUPERVISOR: userId|null, MANAGER, HEAD }].
// Row 0 is the default for every customer; a customer row's filled levels win.
const chainToRows = (chain) => {
    const byScope = new Map([['', { scope: '', SUPERVISOR: null, MANAGER: null, HEAD: null }]]);
    for (const c of chain) {
        if (!byScope.has(c.scope)) byScope.set(c.scope, { scope: c.scope, SUPERVISOR: null, MANAGER: null, HEAD: null });
        byScope.get(c.scope)[c.level] = c.user_id;
    }
    return [...byScope.values()];
};
const rowsToChain = (rows) => rows.flatMap(r => LEVELS.filter(l => r[l])
    .map(l => ({ level: l, scope: r.scope, user_id: r[l] })));

const ChainCard = ({ onSaved }) => {
    const [data, setData] = useState(null);       // { users, customers }
    const [rows, setRows] = useState([]);
    const [saved, setSaved] = useState('[]');     // the chain as last loaded/saved, to know "dirty"
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [adding, setAdding] = useState(null);   // customer being added

    const load = useCallback(async () => {
        try {
            const res = await agentService.getEscalationChain();
            setData(res.data);
            setRows(chainToRows(res.data.chain));
            setSaved(JSON.stringify(rowsToChain(chainToRows(res.data.chain))));
            setError('');
        } catch (e) {
            setError(errText(e, 'Failed to load the escalation chain'));
            setData({ users: [], customers: [] });
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const userById = useMemo(() => new Map((data?.users || []).map(u => [u.id, u])), [data]);
    const dirty = JSON.stringify(rowsToChain(rows)) !== saved;
    const setCell = (i, level, userId) => setRows(rs => rs.map((r, j) => j === i ? { ...r, [level]: userId } : r));

    const save = async () => {
        setBusy(true);
        try {
            await agentService.saveEscalationChain(rowsToChain(rows));
            setSaved(JSON.stringify(rowsToChain(rows)));
            onSaved('Escalation chain saved');
        } catch (e) {
            setError(errText(e, 'Save failed'));
        } finally { setBusy(false); }
    };

    const usedScopes = new Set(rows.map(r => r.scope));
    return (
        <Card variant="outlined" sx={{ borderRadius: '16px', p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Escalation chain</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Each person gets an in-app message, then an in-app call, before the issue moves up.
                        A level left empty is skipped.
                    </Typography>
                </Box>
                <Button variant="outlined" onClick={save} disabled={!dirty || busy}
                    startIcon={busy ? <CircularProgress size={16} /> : null}>
                    {busy ? 'Saving…' : 'Save chain'}
                </Button>
            </Stack>
            {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
            {data === null ? (
                <Skeleton variant="rounded" height={90} />
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '22%' }}>Customer</TableCell>
                                {LEVELS.map((l, i) => (
                                    <TableCell key={l}>{i + 1}. {LEVEL_LABEL[l]}</TableCell>
                                ))}
                                <TableCell sx={{ width: 48 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r, i) => (
                                <TableRow key={r.scope || '*'}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {r.scope || 'All customers'}
                                        </Typography>
                                        {!r.scope && <Typography variant="caption" color="text.secondary">default</Typography>}
                                    </TableCell>
                                    {LEVELS.map(level => {
                                        const fallback = r.scope && rows[0][level] ? userById.get(rows[0][level])?.name : null;
                                        return (
                                            <TableCell key={level}>
                                                <Autocomplete size="small" options={data.users}
                                                    value={r[level] ? userById.get(r[level]) || null : null}
                                                    onChange={(_, u) => setCell(i, level, u ? u.id : null)}
                                                    getOptionLabel={(u) => u.name || ''}
                                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                                    renderOption={(props, u) => (
                                                        <li {...props} key={u.id}>
                                                            <Box>
                                                                <Typography variant="body2">{u.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {[u.role, u.department].filter(Boolean).join(' · ')}
                                                                </Typography>
                                                            </Box>
                                                        </li>
                                                    )}
                                                    renderInput={(p) => (
                                                        <TextField {...p} placeholder={fallback ? `Default: ${fallback}` : 'Nobody'}
                                                            inputProps={{ ...p.inputProps, 'aria-label': `${LEVEL_LABEL[level]} for ${r.scope || 'all customers'}` }} />
                                                    )} />
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell>
                                        {r.scope && (
                                            <Tooltip title="Remove this customer's chain">
                                                <IconButton size="small" aria-label={`Remove ${r.scope}`}
                                                    onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {data !== null && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, maxWidth: 520 }}>
                    <Autocomplete size="small" sx={{ flex: 1 }} value={adding}
                        options={(data.customers || []).filter(c => !usedScopes.has(c))}
                        onChange={(_, c) => setAdding(c)}
                        renderInput={(p) => <TextField {...p} placeholder="A customer with its own people…" />} />
                    <Button startIcon={<AddIcon />} disabled={!adding}
                        onClick={() => { setRows(rs => [...rs, { scope: adding, SUPERVISOR: null, MANAGER: null, HEAD: null }]); setAdding(null); }}>
                        Add customer
                    </Button>
                </Stack>
            )}
        </Card>
    );
};

// ── The page ────────────────────────────────────────────────────────────────
const OperationsIssuesPage = () => {
    const [filter, setFilter] = useState('open');
    const [issues, setIssues] = useState(null);
    const [error, setError] = useState('');
    const [snack, setSnack] = useState('');

    const [raiseOpen, setRaiseOpen] = useState(false);
    const [machines, setMachines] = useState(null);
    const [form, setForm] = useState({ machine: null, severity: 'high', description: '' });
    const [resolving, setResolving] = useState(null);   // issue
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await agentService.getIssues(filter);
            setIssues(res.data || []);
            setError('');
        } catch (e) {
            setError(errText(e, 'Failed to load issues'));
            setIssues(prev => prev || []);
        }
    }, [filter]);
    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const openRaise = async () => {
        setForm({ machine: null, severity: 'high', description: '' });
        setRaiseOpen(true);
        if (machines) return;
        try {
            const res = await agentService.getIssueMachines();
            setMachines(res.data || []);
        } catch (e) {
            setMachines([]);
            setError(errText(e, 'Failed to load machines'));
        }
    };

    const raise = async () => {
        setBusy(true);
        try {
            await agentService.raiseIssue({
                equipment_id: form.machine.equipment_id, severity: form.severity,
                description: form.description.trim(),
            });
            setRaiseOpen(false);
            setSnack('Issue raised — the supervisor has been messaged');
            load();
        } catch (e) {
            setError(errText(e, 'Raising the issue failed'));
        } finally { setBusy(false); }
    };

    const resolve = async () => {
        setBusy(true);
        try {
            await agentService.resolveIssue(resolving.id, note.trim());
            setResolving(null);
            setSnack('Issue resolved');
            load();
        } catch (e) {
            setError(errText(e, 'Resolving the issue failed'));
        } finally { setBusy(false); }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <ChainCard onSaved={setSnack} />

            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                <ToggleButtonGroup size="small" exclusive value={filter}
                    onChange={(_, v) => { if (v) { setFilter(v); setIssues(null); } }}>
                    <ToggleButton value="open">Open</ToggleButton>
                    <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" startIcon={<RaiseIcon />} onClick={openRaise}>Raise issue</Button>
            </Stack>

            {issues === null ? (
                <Stack spacing={1}>{[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={70} />)}</Stack>
            ) : issues.length === 0 ? (
                <Card variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: '16px' }}>
                    <EmptyIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="text.secondary">
                        {filter === 'open' ? 'No open issues' : 'No issues yet'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Silent machines and serious fault codes are raised here automatically.
                        Use “Raise issue” to put one up the ladder yourself.
                    </Typography>
                </Card>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: '70vh' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Machine</TableCell>
                                <TableCell>Issue</TableCell>
                                <TableCell>Ladder</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Raised</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {issues.map(i => (
                                <TableRow key={i.id} hover>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <Typography variant="body2" fontWeight={800}>{i.serial_number || i.equipment_id}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {[i.detail?.model, i.detail?.site].filter(Boolean).join(' · ') || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', maxWidth: 320 }}>
                                        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                                            <Chip size="small" variant="outlined" label={reasonLabel(i)} sx={{ fontWeight: 600 }} />
                                            {i.severity && (
                                                <Chip size="small" color={SEVERITY[i.severity] || 'default'} label={i.severity}
                                                    sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }} />
                                            )}
                                        </Stack>
                                        <Typography variant="body2">{i.title || i.reason}</Typography>
                                        {i.response_text && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                <strong>Reply:</strong> {i.response_text}
                                            </Typography>
                                        )}
                                        {i.resolution_note && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                <strong>Closed:</strong> {i.resolution_note}
                                                {i.resolved_by_name ? ` — ${i.resolved_by_name}` : ''}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}><Ladder issue={i} /></TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <Chip size="small" color={STATUS[i.status]?.c || 'default'}
                                            label={STATUS[i.status]?.l || i.status} sx={{ fontWeight: 700 }} />
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2">{formatToIST(i.created_at, 'dd MMM, HH:mm')}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }} align="right">
                                        {['OPEN', 'ANSWERED'].includes(i.status) && (
                                            <Button size="small" startIcon={<ResolveIcon />}
                                                onClick={() => { setResolving(i); setNote(''); }}>
                                                Resolve
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Raise an issue by hand */}
            <Dialog open={raiseOpen} onClose={() => !busy && setRaiseOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Raise an issue</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Autocomplete options={machines || []} loading={machines === null} value={form.machine}
                            onChange={(_, m) => setForm(f => ({ ...f, machine: m }))}
                            getOptionLabel={(m) => m.serial_number || m.equipment_id}
                            isOptionEqualToValue={(a, b) => a.equipment_id === b.equipment_id}
                            renderOption={(props, m) => (
                                <li {...props} key={m.equipment_id}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>{m.serial_number || m.equipment_id}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {[m.model_number, m.site_location, m.company_name].filter(Boolean).join(' · ')}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(p) => <TextField {...p} label="Machine" />} />
                        <TextField select label="Severity" value={form.severity}
                            onChange={(e) => setForm(f => ({ ...f, severity: e.target.value }))}>
                            <MenuItem value="critical">Critical</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                        </TextField>
                        <TextField label="What is the issue?" multiline rows={3} value={form.description}
                            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
                        <Typography variant="caption" color="text.secondary">
                            The supervisor is messaged now. No answer → a call, then the manager, then the head.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRaiseOpen(false)} disabled={busy}>Cancel</Button>
                    <Button variant="contained" onClick={raise}
                        disabled={busy || !form.machine || !form.description.trim()}
                        startIcon={busy ? <CircularProgress size={16} /> : <RaiseIcon />}>
                        Raise issue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Close an issue */}
            <Dialog open={!!resolving} onClose={() => !busy && setResolving(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Resolve this issue?</DialogTitle>
                <DialogContent dividers>
                    {resolving && (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>{resolving.serial_number || resolving.equipment_id}</strong> — {resolving.title || resolving.reason}
                        </Typography>
                    )}
                    <TextField label="What was done (optional)" multiline rows={2} fullWidth value={note}
                        onChange={(e) => setNote(e.target.value)} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setResolving(null)} disabled={busy}>Cancel</Button>
                    <Button variant="contained" onClick={resolve} disabled={busy}
                        startIcon={busy ? <CircularProgress size={16} /> : <ResolveIcon />}>
                        Resolve
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}
                message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
};

export default OperationsIssuesPage;
